class SeasonInventory extends HTMLElement {
  
  static STORAGE_KEY = 'season-inventory-data';
  static MIN_QUANTITY = 7;
  static MAX_QUANTITY = 23;
  static DECREASE_AMOUNT = 1;
  static MAX_DECREASES = 5;
  
  static STATUS_CLASSES = {
    success: 'text-success',
    warning: 'text-warning',
    error: 'text-error'
  };
  
  constructor() {
    super();
    this.productId = null;
    this.variantId = null;
    this.storageKey = null;
    this.decreaseInterval = 30;
    this.showProgressBar = false;
    this.progressBarMax = 50;
  }
  
  connectedCallback() {
    this.initializeProperties();
    this.cacheElements();
    this.initializeInventory();
    this.listenToVariantChanges();
  }
  
  disconnectedCallback() {
    if (this.variantChangeHandler) {
      document.removeEventListener('variant:change', this.variantChangeHandler);
    }
  }
  
  initializeProperties() {
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.showProgressBar = this.dataset.showProgressBar === 'true';
    this.progressBarMax = parseInt(this.dataset.progressBarMax) || 50;
    this.decreaseInterval = parseInt(this.dataset.decreaseInterval) || 30;
    this.storageKey = `product-${this.productId}-variant-${this.variantId}`;
  }
  
  cacheElements() {
    this.textElement = this.querySelector('[data-inventory-text]');
    this.progressBarElement = this.querySelector('[data-progress-bar]');
  }
  
  initializeInventory() {
    const data = this.getStorageData();
    
    if (!data) {
      this.createNewInventoryRecord();
    } else {
      this.processExistingInventory(data);
    }
  }
  
  createNewInventoryRecord() {
    const quantity = this.generateRandomQuantity();
    const record = {
      quantity: quantity,
      lastVisit: Date.now(),
      initialQuantity: quantity,
      decreaseCount: 0
    };
    
    this.saveToStorage(record);
    this.updateDisplay(quantity);
  }
  
  processExistingInventory(data) {
    const currentTime = Date.now();
    const timeDiff = (currentTime - data.lastVisit) / 1000;
    const decreaseCount = data.decreaseCount || 0;
    
    if (timeDiff >= this.decreaseInterval && decreaseCount < SeasonInventory.MAX_DECREASES) {
      const newQuantity = Math.max(0, data.quantity - SeasonInventory.DECREASE_AMOUNT);
      
      const updatedRecord = {
        ...data,
        quantity: newQuantity,
        lastVisit: currentTime,
        decreaseCount: decreaseCount + 1
      };
      
      this.saveToStorage(updatedRecord);
      this.updateDisplay(newQuantity);
    } else {
      this.updateDisplay(data.quantity);
    }
  }
  
  updateDisplay(quantity) {
    this.updateText(quantity);
    this.updateStatusClass(quantity);
    
    if (this.showProgressBar && this.progressBarElement) {
      this.updateProgressBar(quantity);
    }
  }
  
  updateText(quantity) {
    const strings = (window.themeVariables && window.themeVariables.strings) || {}.inventory || {};

    if (quantity <= 0) {
      this.textElement.textContent = strings.outOfStock || '';
    } else {
      this.textElement.textContent = (strings.limitedAtPrice || '').replace('__COUNT__', quantity);
    }
  }
  
  updateStatusClass(quantity) {
    Object.values(SeasonInventory.STATUS_CLASSES).forEach(cls => {
      this.classList.remove(cls);
    });
    
    if (quantity <= 0) {
      this.classList.add(SeasonInventory.STATUS_CLASSES.error);
    } else if (quantity <= 5) {
      this.classList.add(SeasonInventory.STATUS_CLASSES.warning);
    } else {
      this.classList.add(SeasonInventory.STATUS_CLASSES.success);
    }
  }
  
  updateProgressBar(quantity) {
    const progress = quantity / this.progressBarMax;
    this.progressBarElement.style.setProperty('--progress', progress);
    this.progressBarElement.setAttribute('aria-valuenow', quantity);
  }
  
  getStorageData() {
    try {
      const allData = localStorage.getItem(SeasonInventory.STORAGE_KEY);
      if (!allData) return null;
      
      const parsed = JSON.parse(allData);
      return parsed[this.storageKey] || null;
    } catch (e) {
      console.error('Season Inventory: localStorage read error', e);
      return null;
    }
  }
  
  saveToStorage(record) {
    try {
      let allData = localStorage.getItem(SeasonInventory.STORAGE_KEY);
      allData = allData ? JSON.parse(allData) : {};
      
      allData[this.storageKey] = record;
      
      localStorage.setItem(SeasonInventory.STORAGE_KEY, JSON.stringify(allData));
    } catch (e) {
      console.error('Season Inventory: localStorage write error', e);
    }
  }
  
  generateRandomQuantity() {
    return Math.floor(
      Math.random() * (SeasonInventory.MAX_QUANTITY - SeasonInventory.MIN_QUANTITY + 1)
    ) + SeasonInventory.MIN_QUANTITY;
  }
  
  listenToVariantChanges() {
    this.variantChangeHandler = (event) => {
      if (event.detail.variant) {
        this.variantId = event.detail.variant.id;
        this.storageKey = `product-${this.productId}-variant-${this.variantId}`;
        this.initializeInventory();
      }
    };
    
    document.addEventListener('variant:change', this.variantChangeHandler);
  }
}

if (!window.customElements.get('season-inventory')) {
  window.customElements.define('season-inventory', SeasonInventory);
}

